'use client';

import { useEffect } from 'react';

import { useRouter } from 'next/navigation';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';

import {
  registerPushDeviceToken,
  unregisterPushDeviceToken,
} from '~/(tenant)/_lib/server/push-token-server-actions';

import {
  type CapacitorNativePlatform,
  getNativeOsVersion,
} from './capacitor-device-info';

interface PluginListenerHandle {
  remove: () => Promise<void> | void;
}

interface PushNotificationsPlugin {
  checkPermissions: () => Promise<{ receive: string }>;
  requestPermissions: () => Promise<{ receive: string }>;
  register: () => Promise<void>;
  unregister: () => Promise<void>;
  addListener: (
    event:
      | 'registration'
      | 'registrationError'
      | 'pushNotificationActionPerformed',
    handler: (payload: never) => void,
  ) => PluginListenerHandle | Promise<PluginListenerHandle>;
}

interface AppPlugin {
  getInfo?: () => Promise<{ id?: string } | null>;
}

interface CapacitorGlobal {
  Plugins?: { PushNotifications?: PushNotificationsPlugin; App?: AppPlugin };
  isNativePlatform?: () => boolean;
  isPluginAvailable?: (name: string) => boolean;
  getPlatform?: () => string;
}

interface RegistrationToken {
  value: string;
}

interface NotificationAction {
  notification?: { data?: Record<string, unknown> };
}

// Module-scoped: permission check + register() must run once per app
// session, not once per React mount (HMR, layout remounts).
let pushSetupStarted = false;
let pushSetupUserId: string | null = null;
let pushAuthGeneration = 0;

const PUSH_TOKEN_STORAGE_KEY = 'capacitor-push-device-token';

/**
 * Native push bootstrap for the Capacitor shells:
 *
 *  - waits for an authenticated session, then asks for notification
 *    permission and registers with APNs/FCM
 *  - forwards the resulting device token to the server (upsert keyed on
 *    token; re-running on every launch doubles as the last_seen refresh)
 *  - routes notification taps through the Next.js router using the payload
 *    `url` key — the same path-only routing as CapacitorDeepLinkHandler
 *
 * Guarded by isPluginAvailable: app-store builds that predate the push
 * plugin run this same remote JS bundle and must silently no-op. No-op on
 * the web too — browsers never see the OS prompt.
 */
export function CapacitorPushNotificationsHandler() {
  const router = useRouter();
  const supabase = useSupabase();

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cap = (window as any).Capacitor as CapacitorGlobal | undefined;

    if (!cap?.isNativePlatform?.()) return;
    if (!cap.isPluginAvailable?.('PushNotifications')) return;

    const push = cap.Plugins?.PushNotifications;
    if (!push) return;

    let currentToken: string | null = null;
    let signOutInProgress = false;
    let pendingRegistration = Promise.resolve();
    let successfulRegistration: string | null = null;
    const registrationScope = () =>
      JSON.stringify([
        window.location.origin,
        new URLSearchParams(window.location.search).get('tenant'),
      ]);

    try {
      currentToken = window.localStorage.getItem(PUSH_TOKEN_STORAGE_KEY);
    } catch {
      // Private browsing/storage restrictions must not block registration.
    }

    const forgetCurrentToken = () => {
      currentToken = null;

      try {
        window.localStorage.removeItem(PUSH_TOKEN_STORAGE_KEY);
      } catch {
        // The in-memory token has still been cleared for this app session.
      }
    };

    const routeFromPayload = (raw: unknown) => {
      if (typeof raw !== 'string' || raw.length === 0) return;
      try {
        if (raw.startsWith('/')) {
          router.push(raw);
          return;
        }
        // Absolute URLs route by path only — the webview is already on the
        // tenant origin; never swap it.
        const url = new URL(raw);
        router.push(`${url.pathname}${url.search}${url.hash}`);
      } catch {
        // Malformed target — ignore rather than crash the shell.
      }
    };

    const handles: {
      cancelled: boolean;
      removers: Array<() => Promise<void> | void>;
    } = { cancelled: false, removers: [] };

    const addListener = (
      event: Parameters<PushNotificationsPlugin['addListener']>[0],
      handler: (payload: never) => void,
    ) => {
      Promise.resolve(push.addListener(event, handler))
        .then((listener) => {
          if (handles.cancelled) {
            void listener.remove();
          } else {
            handles.removers.push(listener.remove);
          }
        })
        .catch(() => {
          // Older plugin versions can throw here; safe to ignore.
        });
    };

    const registerToken = async (
      token: string,
      userId: string | null,
      generation: number,
      scope: string,
    ) => {
      // A native registration callback can arrive after Supabase has already
      // emitted SIGNED_OUT. Never retain or bind that stale callback for the
      // next account.
      const isCurrent = () =>
        !handles.cancelled &&
        !signOutInProgress &&
        !!userId &&
        pushSetupUserId === userId &&
        pushAuthGeneration === generation &&
        registrationScope() === scope;

      if (!isCurrent()) return;

      currentToken = token;

      try {
        window.localStorage.setItem(PUSH_TOKEN_STORAGE_KEY, token);
      } catch {
        // The in-memory value still covers sign-out during this app session.
      }

      const info = await cap.Plugins?.App?.getInfo?.();
      const platform: CapacitorNativePlatform =
        cap.getPlatform?.() === 'android' ? 'android' : 'ios';
      const input = {
        token,
        platform,
        bundleId: info?.id ?? 'unknown',
        osVersion: getNativeOsVersion(navigator.userAgent, platform),
      };
      const identity = JSON.stringify([
        scope,
        generation,
        userId,
        input,
      ]);

      if (!isCurrent() || successfulRegistration === identity) return;

      successfulRegistration = null;
      const result = await registerPushDeviceToken(input);

      if (!result.success) {
        throw new Error(result.error);
      }

      if (isCurrent() && result.userId === userId) {
        successfulRegistration = identity;
      }
    };

    const queueTokenRegistration = (token: string) => {
      const userId = pushSetupUserId;
      const generation = pushAuthGeneration;
      const scope = registrationScope();
      const registration = pendingRegistration.then(() =>
        registerToken(token, userId, generation, scope),
      );

      pendingRegistration = registration.catch(() => {
        // Registration is retried on the next launch; never surface this.
      });

      return registration;
    };

    addListener('registration', (token: RegistrationToken) => {
      void queueTokenRegistration(token.value).catch(() => {
        // Registration is retried on the next launch; never surface this.
      });
    });

    addListener('registrationError', () => {
      // Denied entitlement / simulator / network — nothing actionable here.
    });

    // Tap routing is attached unconditionally (before auth): a cold-start
    // tap must route even if the session check below is still in flight.
    addListener(
      'pushNotificationActionPerformed',
      (action: NotificationAction) => {
        routeFromPayload(action.notification?.data?.url);
      },
    );

    const trySetup = async (knownUserId?: string) => {
      const startingGeneration = pushAuthGeneration;
      const userId =
        knownUserId ?? (await supabase.auth.getSession()).data.session?.user.id;

      if (
        !userId ||
        handles.cancelled ||
        signOutInProgress ||
        startingGeneration !== pushAuthGeneration
      ) {
        return;
      }
      if (pushSetupStarted && pushSetupUserId === userId) return;

      if (pushSetupUserId !== userId) {
        pushSetupStarted = false;
        pushAuthGeneration += 1;
        successfulRegistration = null;
      }

      pushSetupStarted = true;
      pushSetupUserId = userId;
      const generation = pushAuthGeneration;
      const isCurrent = () =>
        !signOutInProgress &&
        pushAuthGeneration === generation;

      try {
        // Rebind a token retained across WebView reloads or account switches
        // before asking APNs/FCM to emit it again.
        if (currentToken) {
          await queueTokenRegistration(currentToken);
        }

        if (!isCurrent()) return;
        const status = await push.checkPermissions();
        if (!isCurrent()) return;

        if (status.receive === 'prompt') {
          const requested = await push.requestPermissions();
          if (!isCurrent()) return;
          if (requested.receive !== 'granted') return;
        } else if (status.receive !== 'granted') {
          return;
        }

        await push.register();
      } catch {
        // Allow a retry on the next launch.
        if (isCurrent()) {
          pushSetupStarted = false;
        }
      }
    };

    void trySetup();

    const originalSignOut = supabase.auth.signOut;
    const wrappedSignOut: typeof originalSignOut = async (...args) => {
      if (args[0]?.scope === 'others') {
        return originalSignOut.apply(supabase.auth, args);
      }

      signOutInProgress = true;
      pushAuthGeneration += 1;
      successfulRegistration = null;

      try {
        await pendingRegistration;

        if (currentToken) {
          try {
            const result = await unregisterPushDeviceToken({
              token: currentToken,
            });

            if (!result.success) {
              throw new Error(result.error);
            }
          } catch {
            // The OS unregister below is the privacy fallback when server-side
            // token cleanup is unavailable.
          }
        }

        try {
          // Remove this app's APNs/FCM registration before auth is cleared.
          // The next SIGNED_IN setup calls register() again.
          await push.unregister();
        } catch {
          // Signing out must still proceed if native cleanup is unavailable.
        }

        forgetCurrentToken();

        return await originalSignOut.apply(supabase.auth, args);
      } finally {
        pushSetupStarted = false;
        pushSetupUserId = null;
        signOutInProgress = false;
      }
    };

    supabase.auth.signOut = wrappedSignOut;

    const { data: authSub } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === 'SIGNED_OUT') {
          pushAuthGeneration += 1;
          successfulRegistration = null;
          pushSetupStarted = false;
          pushSetupUserId = null;

          if (!signOutInProgress) {
            // Session expiry and remote revocation bypass the signOut wrapper,
            // so at least stop native delivery and discard the stale token.
            forgetCurrentToken();
            void push.unregister().catch(() => undefined);
          }

          return;
        }

        if (event === 'SIGNED_IN' && session) {
          if (pushSetupUserId !== session.user.id) {
            pushSetupStarted = false;
          }

          void trySetup(session.user.id);
        }
      },
    );

    return () => {
      handles.cancelled = true;
      for (const remove of handles.removers) void remove();
      authSub.subscription.unsubscribe();

      if (supabase.auth.signOut === wrappedSignOut) {
        supabase.auth.signOut = originalSignOut;
      }
    };
  }, [router, supabase]);

  return null;
}
